import { useLabels } from '../../hooks/useLabels';

interface Props {
  value: string[];
  onChange: (labelIds: string[]) => void;
  /** Field caption; omit to render the pills alone. */
  label?: string;
  /** Smaller pills for dense rows (the template editor). */
  size?: 'sm' | 'md';
}

/**
 * The label pills of the task form, as a controlled field: every global label
 * as a toggle, coloured when on. Renders nothing while there are no labels
 * defined at all. Used by the task create/edit form and the task-template
 * editor, so a template's item offers exactly the labels a task would.
 */
export function LabelPicker({ value, onChange, label, size = 'md' }: Props) {
  const { data: labels } = useLabels();
  if (!labels || labels.length === 0) return null;

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  const pills = (
    <div className={`flex flex-wrap ${size === 'sm' ? 'gap-1' : 'gap-1.5'}`}>
      {labels.map((l) => {
        const active = value.includes(l.id);
        return (
          <button
            key={l.id}
            type="button"
            onClick={() => toggle(l.id)}
            className={`rounded-full border transition ${
              size === 'sm' ? 'px-1.5 py-px text-[11px]' : 'px-2 py-0.5 text-xs'
            } ${active ? 'border-transparent' : 'border-gray-300 opacity-50 hover:opacity-100'}`}
            style={active ? { backgroundColor: l.color, color: '#fff' } : undefined}
            aria-pressed={active}
          >
            {l.name}
          </button>
        );
      })}
    </div>
  );

  if (!label) return pills;
  return (
    <div className="flex flex-col text-sm font-medium text-gray-700">
      {label}
      <div className="mt-1.5">{pills}</div>
    </div>
  );
}
